"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Cpu, CircleCheck as CheckCircle2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const DEFAULT_MASCOT_SRC = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png";
const CONTENT = {
  specRail: {
    en: "Milestone Showcase // Capstone Track",
    vi: "Trưng bày dự án thực chiến // Lộ trình Capstone"
  },
  capstoneBadge: {
    en: "STAGE 04 CAPSTONE BUILD",
    vi: "DỰ ÁN TỐT NGHIỆP CAPSTONE CẤP ĐỘ 04"
  },
  capstoneHardware: {
    title: {
      en: "4-Axis Programmable Kinematic Arm",
      vi: "Cánh tay Robot động học 4 trục lập trình"
    },
    ageTag: {
      en: "Ages 13–16 Capstone",
      vi: "Dự án Capstone 13–16 tuổi"
    },
    desc: {
      en: "Engineered using high-torque metal gear servos, custom 3D-modeled brackets, and autonomous pick-and-place logic compiled in C++ and Python.",
      vi: "Được Engineering với động cơ servo bánh răng kim loại lực kéo cao, khung cơ khí thiết kế 3D và thuật toán tự động gắp - đặt viết bằng C++ và Python."
    }
  },
  metrics: [{
    value: "100%",
    label: {
      en: "Original Student Code",
      vi: "Code do học sinh tự lập trình"
    }
  }, {
    value: "1:6",
    label: {
      en: "Lab Mentor Ratio",
      vi: "Tỉ lệ 1 cố vấn : 6 học sinh"
    }
  }, {
    value: "Portfolio",
    label: {
      en: "Competition Verified",
      vi: "Kiểm chứng qua các kỳ thi"
    }
  }],
  heading: {
    en: "What your child actually builds",
    vi: "Con bạn sẽ tự tay chế tạo những gì?"
  },
  subheading: {
    en: "Every stage ends with a working machine they code, wire, and present.",
    vi: "Mỗi cấp độ đều kết thúc bằng một cỗ máy hoàn chỉnh do con tự Coding, đấu nối và thuyết trình."
  },
  narrative: {
    en: "At ROBOTO, learning is demonstrated through physical proof rather than multiple-choice quizzes. From their first battery-driven rover to multi-axis inverse kinematics, every curriculum stage produces an original portfolio build that students test on competition tables and bring home to demonstrate.",
    vi: "Tại ROBOTO, năng lực học viên được chứng minh qua sản phẩm thực tế thay vì những bài trắc nghiệm lý thuyết. Từ chiếc xe tự hành đầu tiên đến hệ thống động học ngược đa trục, mỗi giai đoạn trong lộ trình STEM / STEAM đều mang lại sản phẩm độc bản để học sinh thử nghiệm trên bàn thi đấu và tự tin mang về nhà trình diễn."
  },
  milestonesList: [{
    phase: {
      en: "Stage 01 (Ages 4–6)",
      vi: "Cấp độ 01 (4–6 tuổi)"
    },
    title: {
      en: "Motorized Mechanical Gizmos",
      vi: "Cỗ máy cơ khí truyền động LEGO"
    },
    description: {
      en: "Young learners snap together sensory gears, pulleys, and basic tactile motors, understanding basic cause-and-effect mechanics.",
      vi: "Học viên nhỏ tuổi lắp ráp bánh răng, ròng rọc và motor xúc giác từ LEGO Education, thấu hiểu nguyên lý cơ học nhân - quả nền tảng của Robotics."
    }
  }, {
    phase: {
      en: "Stage 02 (Ages 7–9)",
      vi: "Cấp độ 02 (7–9 tuổi)"
    },
    title: {
      en: "Automated Obstacle Rovers",
      vi: "Robot thám hiểm tự hành tránh vật cản"
    },
    description: {
      en: "Students program ultrasonic sensor loops and dual-motor drives in block code to steer independently through dynamic mazes.",
      vi: "Học sinh Coding bằng khối lệnh trực quan, tích hợp vòng lặp cảm biến siêu âm và cụm motor kép để Robot độc lập vượt qua mê cung biến đổi."
    }
  }, {
    phase: {
      en: "Stage 03 (Ages 10–12)",
      vi: "Cấp độ 03 (10–12 tuổi)"
    },
    title: {
      en: "IoT Smart Greenhouse Stations",
      vi: "Trạm nhà kính thông minh kết nối IoT"
    },
    description: {
      en: "Builders wire microcontrollers to ambient humidity and light sensors, reading live telemetry into custom Python scripts.",
      vi: "Học sinh đấu nối vi điều khiển với cảm biến độ ẩm và ánh sáng quang học, phân tích dữ liệu trực tiếp bằng các đoạn mã Python tự viết."
    }
  }, {
    phase: {
      en: "Stage 04 (Ages 13–16)",
      vi: "Cấp độ 04 (13–16 tuổi)"
    },
    title: {
      en: "Precision Capstone Robotic Arm",
      vi: "Cánh tay Robot động học chính xác cao"
    },
    description: {
      en: "Senior students design 4-axis kinematics, solder driver boards, and deploy inverse kinematic code with feedback grippers.",
      vi: "Học viên lớn tự thiết kế cấu trúc động học 4 trục, hàn bảng mạch điều khiển và triển khai giải thuật điều khiển kẹp phản hồi với AI cơ bản."
    }
  }],
  buttons: {
    projects: {
      en: "View Student Projects",
      vi: "Xem các dự án của học sinh"
    },
    freeTrial: {
      en: "Book a Free Lab Trial",
      vi: "Đăng ký buổi trải nghiệm Lab miễn phí"
    }
  }
};
export default function Milestones() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getStoredLang = (): "vi" | "en" => {
      if (typeof window === "undefined") return "vi";
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      } catch {
        // Fallback gracefully
      }
      return "vi";
    };
    setLang(getStoredLang());
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const containerAnimation = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="sample-projects-milestones" data-nav="light" className="relative w-full bg-background py-16 md:py-24 border-b border-border overflow-hidden">
      {/* Precision Spec Rail Motif */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 mb-8">
        <div className="flex items-center gap-2.5">
          <span className="size-1.5 bg-secondary shrink-0" />
          <div className="h-px w-20 bg-secondary" />
          <span className="font-mono text-xs font-bold tracking-widest text-secondary uppercase">
            {CONTENT.specRail[lang]}
          </span>
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left Column: Authentic Capstone Photograph & Spec Callout */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-6 flex flex-col gap-6">
            <div className="relative group rounded-xl border border-border bg-card p-2 md:p-3 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-md overflow-hidden">
              {/* Corner Engineering Bracket Accent */}
              <div className="absolute top-4 left-4 z-10 rounded-md bg-primary/95 px-3 py-1.5 backdrop-blur-xs flex items-center gap-2 border border-primary/20 shadow-xs">
                <Cpu className="size-4 text-secondary" />
                <span className="font-mono text-xs font-semibold text-primary-foreground tracking-wide uppercase">
                  {CONTENT.capstoneBadge[lang]}
                </span>
              </div>

              <div className="relative aspect-4/3 w-full overflow-hidden rounded-lg bg-muted border border-border/50">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_testing_robotic_arm_gripper.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_testing_robotic_arm_gripper.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_testing_robotic_arm_gripper.png-wvc-sizes" alt="Student testing a custom microcontroller-driven robotic arm with precision gripper in the robotics laboratory" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
              </div>

              {/* Hardware Spec Card below photo */}
              <div className="p-4 md:p-5 flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <Wrench className="size-4 text-secondary" />
                    <span className="font-serif text-sm md:text-base font-semibold text-foreground">
                      {CONTENT.capstoneHardware.title[lang]}
                    </span>
                  </div>
                  <span className="font-mono text-xs px-2.5 py-0.5 rounded-lg bg-secondary/15 border border-secondary/25 text-secondary font-semibold uppercase tracking-wide">
                    {CONTENT.capstoneHardware.ageTag[lang]}
                  </span>
                </div>
                <p className="font-sans text-xs md:text-sm text-muted-foreground leading-relaxed">
                  {CONTENT.capstoneHardware.desc[lang]}
                </p>
              </div>
            </div>

            {/* Quick Validation Metric Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CONTENT.metrics.map((metric, i) => <div key={metric.value} data-index={i} className={`rounded-xl border border-border bg-card p-3.5 text-center shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-secondary ${i === 2 ? "col-span-2 sm:col-span-1" : ""}`}>
                  <p className="font-serif text-2xl font-bold text-foreground">{metric.value}</p>
                  <p className="font-sans text-xs text-muted-foreground mt-0.5">{metric.label[lang]}</p>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Heading, Structured Narrative & Milestones Breakdown */}
          <motion.div variants={containerAnimation} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-6 flex flex-col gap-6">
            <div>
              <motion.h2 variants={itemAnimation} className="font-serif text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
                {CONTENT.heading[lang]}
              </motion.h2>
              <motion.p variants={itemAnimation} className="mt-3 font-serif text-lg sm:text-xl font-medium text-foreground/80 leading-snug">
                {CONTENT.subheading[lang]}
              </motion.p>
            </div>

            <motion.p variants={itemAnimation} className="font-sans text-base text-muted-foreground leading-relaxed">
              {CONTENT.narrative[lang]}
            </motion.p>

            {/* Structured Stage Milestones List */}
            <motion.div variants={itemAnimation} className="flex flex-col gap-3">
              {CONTENT.milestonesList.map((item, i) => <Card key={i} data-index={i} className="p-4 md:p-5 border border-border bg-card rounded-xl transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-sm">
                  <div className="flex items-start gap-3.5">
                    <div className="mt-0.5 rounded-lg bg-secondary/15 border border-secondary/25 p-2 text-secondary shrink-0 shadow-2xs">
                      <CheckCircle2 className="size-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
                        <span className="font-serif text-sm md:text-base font-semibold text-foreground">
                          {item.title[lang]}
                        </span>
                        <span className="font-mono text-[11px] font-semibold tracking-wider text-muted-foreground uppercase bg-muted/60 px-2 py-0.5 rounded-md border border-border/40">
                          {item.phase[lang]}
                        </span>
                      </div>
                      <p className="font-sans text-xs md:text-sm text-muted-foreground leading-relaxed">
                        {item.description[lang]}
                      </p>
                    </div>
                  </div>
                </Card>)}
            </motion.div>

            {/* Action CTA Cluster */}
            <motion.div variants={itemAnimation} className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-serif font-semibold rounded-lg shadow-sm">
                <Link to="/ProjectsCompetitionsPage">
                  {CONTENT.buttons.projects[lang]}
                  <ArrowUpRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-serif font-semibold rounded-lg shadow-sm">
                <Link to="/FreeTrialContactPage">
                  {CONTENT.buttons.freeTrial[lang]}
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Floating Mascot */}
      <div className="pointer-events-none select-none absolute bottom-4 right-4 sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-10 z-0 hidden sm:block" aria-hidden="true">
        <img src={DEFAULT_MASCOT_SRC} alt="" className="h-20 w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" loading="lazy" decoding="async" />
      </div>
    </section>;
}